/**
 * Regras puras da página /sistema/cowork-ofertas (Cowork Offer Hunter, Fase 1, shadow).
 *
 * - Estados da fase: approved_shadow NÃO é publicação. Não há ação de publicar.
 * - "Origem" abre só a página pública do marketplace: https, hosts públicos de Mercado Livre,
 *   Shopee e Amazon. Nunca o redirector do Super Vitrine, link afiliado ou encurtador,
 *   carrinho ou checkout.
 * - Tudo o que é de validação (frescura, confiança do desconto, claim, variante, match) é só
 *   EXIBIDO como veio da API. Nada é recalculado aqui.
 */
export const ROTA_API = '/admin/cowork';

export const STATUS = {
    pending: { label: 'Pendente', cls: 'sv-badge-gold' },
    approved_shadow: { label: 'Aprovado (shadow)', cls: 'sv-badge-success' },
    rejected: { label: 'Rejeitado', cls: 'sv-badge-muted' },
    duplicate: { label: 'Duplicado', cls: 'sv-badge-muted' },
    expired: { label: 'Expirado', cls: 'sv-badge-muted' },
    needs_review: { label: 'Revisar', cls: 'sv-badge-warning' },
};

// Ações do admin nesta fase. Não existe "publicar" em lugar nenhum.
export const ACOES = [
    { status: 'approved_shadow', label: 'Aprovar para shadow' },
    { status: 'rejected', label: 'Rejeitar' },
    { status: 'needs_review', label: 'Marcar revisão' },
];

export const MARKETPLACES = { mercadolivre: 'Mercado Livre', shopee: 'Shopee', amazon: 'Amazon' };
// Motivos oferecidos no painel para fonte indisponível (subconjunto aceito pela API).
export const MOTIVOS_FONTE = ['captcha', 'login', 'challenge', 'bm-verify', 'error'];

// Rótulos legíveis dos códigos de motivo. Código desconhecido aparece como veio.
export const ROTULOS_MOTIVO = {
    duplicate: 'Já existe no catálogo/crawler',
    weak_match_product_key: 'Título parecido com item existente (match fraco)',
    expired: 'Observação expirada',
    insufficient_discount_confidence: 'Desconto sem confiança suficiente',
    below_quality_floor: 'Abaixo do piso de qualidade',
    insufficient_product_identity: 'Tipo de produto não identificado no título',
    poor_title_quality: 'Qualidade do título baixa',
    essential_attribute_truncated: 'Atributo essencial cortado no título',
    suspicious_price_context: 'Preço de variante incerto',
    claim_risk_medium: 'Claim do vendedor no título',
    restricted_category: 'Categoria restrita nesta fase',
    cooldown: 'Publicado recentemente (cooldown)',
    invalid_price: 'Preço inválido ou ausente',
    discount_mismatch: 'Desconto diverge dos preços',
    suspicious_discount: 'Desconto suspeito',
    suspicious_previous_price: 'Preço anterior suspeito',
    discount_without_previous_price: 'Desconto sem preço anterior',
    unknown_or_invalid_destination: 'Destino inválido',
    stale_or_unknown_freshness: 'Frescura desconhecida ou vencida',
    crawler_price_changed: 'Preço diverge do crawler',
    encoding_problem: 'Problema de codificação no título',
};

export const ROTULOS_MATCH = {
    none: 'Sem match (novo)',
    external_id_discovery: 'Crawler (discovery por ID)',
    external_id_deal: 'Catálogo (deal por ID)',
    product_key: 'Título parecido (fraco)',
    product_key_cowork: 'Candidato Cowork parecido (fraco)',
};

const HOSTS_PUBLICOS = ['mercadolivre.com.br', 'shopee.com.br', 'amazon.com.br'];
const HOSTS_PROIBIDOS = ['supervitrineofertas.com', 'meli.la', 'amzn.to', 's.shopee.com.br', 'shope.ee'];
const CAMINHOS_PROIBIDOS = /\/(cart|carrinho|checkout|gp\/buy|gp\/cart|buy|purchase)(\/|$)|\/r\//i;

const doHost = (host, lista) => lista.some((h) => host === h || host.endsWith(`.${h}`));

/** URL segura para o botão "Origem", ou null. */
export function urlOrigemSegura(valor) {
    if (typeof valor !== 'string') return null;
    let u;
    try { u = new URL(valor); } catch (_) { return null; }
    const host = u.hostname.toLowerCase();
    if (u.protocol !== 'https:' || u.username || u.password || (u.port && u.port !== '443')) return null;
    if (doHost(host, HOSTS_PROIBIDOS) || !doHost(host, HOSTS_PUBLICOS)) return null;
    if (CAMINHOS_PROIBIDOS.test(u.pathname)) return null;
    return `https://${host}${u.pathname}`;
}

export function reais(valor) {
    if (typeof valor !== 'number' || !Number.isFinite(valor)) return '—';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function percentual(valor) {
    // Mostra só o desconto que o Cowork observou; nunca calcula a partir dos preços.
    return typeof valor === 'number' && Number.isFinite(valor) ? `${Math.round(valor)}%` : '—';
}

export const rotuloMotivo = (codigo) => ROTULOS_MOTIVO[codigo] || codigo;

/** Resumo curto dos motivos: o primeiro legível e quantos mais existem. */
export function resumoMotivos(item) {
    const motivos = item?.rejection_reasons || [];
    if (!motivos.length) return { principal: '—', extras: 0, todos: [] };
    return { principal: rotuloMotivo(motivos[0]), extras: motivos.length - 1, todos: motivos.map((m) => ({ codigo: m, rotulo: rotuloMotivo(m) })) };
}

/** Campos de validação exatamente como vieram da API (ausente = '—'). */
export function validacaoExibida(item) {
    const v = item?.validation || {};
    const f = v.freshness || {};
    const texto = (x) => (x === null || x === undefined || x === '' ? '—' : String(x));
    let variante = '—';
    if (v.variant_price_uncertain === true) variante = 'incerto';
    else if (v.variant_price_uncertain === false) variante = 'ok';
    return {
        frescuraOrigem: texto(f.source),
        frescuraConfianca: texto(v.freshness_confidence),
        frescuraIdade: typeof f.age_hours === 'number' ? `${f.age_hours} h` : '—',
        confiancaDesconto: texto(v.discount_confidence),
        claim: texto(v.claim_risk),
        variante,
        match: item?.match_basis ? (ROTULOS_MATCH[item.match_basis] || item.match_basis) : '—',
    };
}

/** Lotes conhecidos, a partir do resumo sem filtro (a API devolve os lotes em `sources`). */
export function lotesDoResumo(resumo) {
    return (resumo?.sources || []).map((l) => l.run_id).filter(Boolean);
}

/** Contadores de um resumo (geral ou de um lote), só com o que a API contou. */
export function contadoresDoResumo(resumo) {
    const s = resumo?.by_status || {};
    const c = resumo?.crawler_comparison || {};
    return {
        encontrados: resumo?.found ?? 0,
        approved_shadow: s.approved_shadow ?? 0,
        rejected: s.rejected ?? 0,
        duplicate: s.duplicate ?? 0,
        needs_review: s.needs_review ?? 0,
        expired: s.expired ?? 0,
        pending: s.pending ?? 0,
        novos: c.new ?? 0,
        conhecidos: c.already_known ?? 0,
        fracos: c.weak_title_matches ?? 0,
    };
}

/** Lê o JSON colado na ingestão: aceita uma lista ou { candidates: [...] }. Máximo 30. */
export function lerLote(texto) {
    let dados;
    try { dados = JSON.parse(texto); } catch (_) { return { erro: 'JSON inválido' }; }
    const lista = Array.isArray(dados) ? dados : dados?.candidates;
    if (!Array.isArray(lista) || lista.length === 0) return { erro: 'Envie uma lista de candidatos' };
    if (lista.length > 30) return { erro: 'Máximo de 30 candidatos por lote' };
    return { candidatos: lista };
}
